import Media from '@components/ui/Media/Media'
import { useGSAP } from '@gsap/react'
import { ProductDetailFragment } from '@lib/graphql/graphql'
import { MediaWithCropsFragment } from '@lib/umbraco/generated/types.generated'
import { generateMediaUrlV2 } from '@lib/utils'
import { mapEdgesToItems } from '@utils/map'
import clsx from 'clsx'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import TextPlugin from 'gsap/dist/TextPlugin'
import { useQueryState } from 'next-usequerystate'
import { useRouter } from 'next/router'
import { CSSProperties, FC, useEffect, useState } from 'react'
import s from './ProductScrollInformation.module.scss'

type ProductScrollInformationProps = {
  product: ProductDetailFragment
  extraProperties: {
    comfortLevel: number
    comfortText: string
  }
}

const ProductScrollInformation: FC<ProductScrollInformationProps> = ({
  product,
  extraProperties: { comfortLevel, comfortText },
}) => {
  /*eslint-disable*/
  if (!product) return

  const router = useRouter()
  const { itemNumber } = router.query

  const variants = mapEdgesToItems(product.variants)

  const [itemNumberQuery, _] = useQueryState('itemNumber', {
    defaultValue: (itemNumber as string) ?? product.heroVariant.sku.toString(),
  })

  const [variant, setVariant] = useState(
    variants.find((x) => x.sku === itemNumber),
  )
  useEffect(() => {
    // set the variant
    if (!itemNumberQuery) return

    setVariant(variants.find((x) => x.sku === itemNumberQuery))
  }, [itemNumberQuery])

  // using GSAP scroll trigger to animate the upholsteryLines containers
  useGSAP(() => {
    gsap.registerPlugin(ScrollTrigger)
    gsap.registerPlugin(TextPlugin)
    // create timeline and stagger the animation of the lines
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: `.${s.root}`,
        // the start should be 48px before the root element
        start: 'top-=48px',
        end: '+=2000%',
        toggleActions: 'play none none reverse',
        pin: true,
        snap: {
          snapTo: 'labelsDirectional',
          duration: {
            min: 2,
            max: 4,
          },
        },
        scrub: 0,
      },
    })

    tl.addLabel('start')

      .fromTo(
        document.querySelectorAll(`.${s.upholsteryLines} .${s.line}`),
        {
          scaleX: 0,
          opacity: 0,
          stagger: 0.5,
        },
        {
          scaleX: 1,
          opacity: 1,
          stagger: 0.5,
        },
        '<+=0.2',
      )
      .fromTo(
        document.querySelectorAll(`.${s.upholsteryLines} .${s.text}`),
        {
          opacity: 0,
          stagger: 0.5,
        },
        {
          opacity: 1,
          stagger: 0.5,
        },
        '<+=0.2',
      )
      .addLabel('materials')

      .to(document.querySelectorAll(`.${s.upholsteryLines} .${s.text}`), {
        opacity: 0,
        stagger: 0.5,
      })

      .to(document.querySelectorAll(`.${s.upholsteryLines} .${s.line}`), {
        scaleX: 0,
        opacity: 0,
        stagger: 0.5,
      })
      .to(`.${s.productLine} span`, {
        text: 'Sitting Comfort',
      })

      .fromTo(
        `.${s.comfortBarWrapper}`,
        {
          opacity: 0,
          visibility: 'hidden',
        },
        {
          opacity: 1,
          visibility: 'visible',
        },
      )

      .fromTo(
        `.${s.comfortBarWrapper} .${s.upper}`,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          text: comfortText ?? 'Comfort Text missing',
        },
      )

      .to(`.${s.comfortBar}`, {
        width: `${comfortLevel ?? 0}%`,
      })
      .addLabel('sittingComfort')

      // animate the comfortBar out
      .to(`.${s.comfortBarWrapper} .${s.upper}`, {
        opacity: 0,
      })

      .to(`.${s.comfortBarWrapper}`, {
        opacity: 0,
      })

      .to(`.${s.productLine} span`, {
        text: 'Specifications',
      })

      .fromTo(
        `.${s.specsWrapper}`,
        {
          opacity: 0,
          visibility: 'hidden',
        },
        {
          opacity: 1,
          visibility: 'visible',
        },
      )

      .fromTo(
        `.${s.specsWrapper} .${s.partialHeight}`,
        {
          scaleY: 0,
          opacity: 0,
        },
        {
          scaleY: 1,
          opacity: 1,
        },
      )

      .fromTo(
        `.${s.specsWrapper} .${s.fullHeight}`,
        {
          scaleY: 0,
          opacity: 0,
        },
        {
          scaleY: 1,
          opacity: 1,
        },
        '<+=0.4',
      )
      .fromTo(
        `.${s.specsWrapper} .${s.text}`,
        {
          opacity: 0,
          stagger: 0.25,
        },
        {
          opacity: 1,
          stagger: 0.25,
        },
        '<+=0.8',
      )
      .addLabel('specifications')

    function labelsToProgress(timeline, labels) {
      let progress = []
      labels.forEach((label) =>
        progress.push(timeline.labels[label] / timeline.duration()),
      )
      return progress
    }
  })

  // find upholstery based on attributeValue with name 'Upholstery'
  const upholstery = variant.selectedAttributes.find((attributeValue) =>
    attributeValue.name.includes('Uphol'),
  )

  // find shellMaterial based on attributeValue with name 'Shell Material'
  const shellMaterial = variant.selectedAttributes.find((attributeValue) =>
    attributeValue.name.includes('Shell'),
  )

  // find base based on attributeValue with name 'Base'
  const base = variant.selectedAttributes.find((attributeValue) =>
    attributeValue.name.includes('Base'),
  )

  // find seatHeight based on metadata with name spec_seatHeight
  let seatHeight = variant.metadata
    .find((metadata) => metadata.key.includes('spec_seatHeight'))
    ?.value.toLowerCase()

  if (!seatHeight?.includes('cm')) seatHeight = seatHeight?.concat(' cm')

  // find totalHeight based on metadata with name spec_seatingDimensionsDxWxH (height is the last value)
  let totalHeight = variant.metadata
    .find((metadata) => metadata.key.includes('spec_seatingDimensionsDxWxH'))
    ?.value.split('x')[2]
    .toLowerCase()

  if (!totalHeight?.includes('cm')) totalHeight = totalHeight?.concat(' cm')

  return (
    <div className={s.root}>
      <div className={s.wrapper}>
        <div className={s.productDetails}>
          <div className={s.productName}>{product.name}</div>
          <div className={s.productLine}>
            The <span>Materials</span>
          </div>
        </div>
        <Media
          mediaWithCrops={
            {
              __typename: 'MediaWithCrops',
              url: generateMediaUrlV2(variant.media[0].src),
              media: {
                __typename: 'Image',
                url: generateMediaUrlV2(variant.media[0].src),
              },
            } as MediaWithCropsFragment
          }
          disableShopTheLook
          mediaClassName={s.productImage}
          sizes="(max-width: 768px) 100vw, 40vw"
        >
          <div className={s.upholsteryLines}>
            {upholstery && (
              <div className={s.leftContainer}>
                <div className={s.line} />
                <div className={s.text}>
                  <span>Upholstery</span>
                  {upholstery.value}
                </div>
              </div>
            )}
            {shellMaterial && (
              <div className={clsx(s.rightContainer, s.upper)}>
                <div className={s.line} />
                <div className={s.text}>
                  <span>Shell Material</span>
                  {shellMaterial.value}
                </div>
              </div>
            )}
            {base && (
              <div className={clsx(s.rightContainer, s.lower)}>
                <div className={s.line} />
                <div className={s.text}>
                  <span>Base</span>
                  {base.value}
                </div>
              </div>
            )}
          </div>

          <div
            className={s.comfortBarWrapper}
            style={{ '--comfort-level': `${comfortLevel}%` } as CSSProperties}
          >
            <div className={s.textWrapper}>
              <div className={s.upper} />
            </div>

            <div className={s.comfortBar} />
          </div>

          <div className={s.specsWrapper}>
            {seatHeight && (
              <div className={s.partialHeight}>
                <div className={s.text}>
                  <span>Seat height</span>
                  <br />
                  {seatHeight}
                </div>
              </div>
            )}
            {totalHeight && (
              <div className={s.fullHeight}>
                <div className={s.text}>
                  <span>Total height</span>
                  <br />
                  {totalHeight}
                </div>
              </div>
            )}
          </div>
        </Media>
        {/* {product?.arFile && (
          <ARButtonWrapper
            iosSrc={`https://media.gubi.com/${product.arFile.s3FileName}`}
          >
            <span className={s['ar-icon']}>
              <span className={s['ar-icon-text']}>Explore with</span>
              <ArIcon />
            </span>
          </ARButtonWrapper>
        )} */}
      </div>
    </div>
  )
}

export default ProductScrollInformation
